import React, { useEffect, useRef, useState } from "react";
import { FiPrinter, FiSend } from "react-icons/fi";
import { useReactToPrint } from "react-to-print";
import { T, btnStyleOutline, btnStylePrimary, localToday } from "../Sales/SalesShared";
import { SendDocumentDialog, canSendDocumentType, isSendable } from "./SendDocumentDialog";

/* Print / Save PDF for several documents at once on the Sales, Purchasing and
   Finance lists. Each ticked document is loaded in full (the list rows don't
   carry line items), drawn with the same print template as its own page, and
   everything goes to the browser's print dialog as one job, one document per
   page. "Save as PDF" in that dialog then gives a single PDF. */

// Same rule as the Print button on a document's own page.
export const isPrintable = (row) => !["Cancelled", "Void"].includes(row.status);

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

export function BulkPrintBar({
    selectedRows,
    onClear,
    fetchById,
    Template,
    docType,
    companyProfile,
    forceNegativeTotals = false,
    // Fills in fields the page normally defaults (e.g. phoneNumber on Sales).
    prepareData = (doc) => doc,
    // "customer" or "supplier": adds Send Selected (emails each document to
    // its own contact) when this document type can be sent.
    sendParty = null,
    // More bulk buttons from the page (e.g. Create DO on the Invoice list).
    children = null,
}) {
    const [loading, setLoading] = useState(false);
    const [docs, setDocs] = useState(null);
    const [message, setMessage] = useState("");
    const [sendDocs, setSendDocs] = useState(null);
    const [sendNotice, setSendNotice] = useState(null);
    const printRef = useRef(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const print = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `${docType}s-${localToday()}`,
        onAfterPrint: () => {
            if (mounted.current) setDocs(null);
        },
    });

    // Print once the loaded documents are on the page.
    useEffect(() => {
        if (docs?.length) print();
    }, [docs]); // eslint-disable-line react-hooks/exhaustive-deps

    if (selectedRows.length === 0 && !loading && !sendDocs) return null;

    const canSend = Boolean(sendParty) && canSendDocumentType(docType);
    const sendable = selectedRows.filter(isSendable);

    // Each document in full (list rows have no line items).
    const loadDocs = async (rows, verb) => {
        setLoading(true);
        setMessage("");
        setSendNotice(null);
        const results = await Promise.allSettled(rows.map((row) => fetchById(row.id)));
        if (!mounted.current) return [];

        const loaded = [];
        const failed = [];
        results.forEach((result, index) => {
            if (result.status === "fulfilled" && result.value) loaded.push(prepareData(result.value));
            else failed.push(rows[index].id);
        });
        setLoading(false);
        if (failed.length) {
            setMessage(
                loaded.length
                    ? `Could not load ${failed.join(", ")}; ${verb} the other ${plural(loaded.length, "document")}.`
                    : `Could not load ${failed.join(", ")}. Please try again.`
            );
        }
        return loaded;
    };

    const start = async () => {
        if (loading) return;
        const loaded = await loadDocs([...selectedRows], "printing");
        if (loaded.length) setDocs(loaded);
    };

    const startSend = async () => {
        if (loading || !sendable.length) return;
        const loaded = await loadDocs([...sendable], "sending");
        if (loaded.length) setSendDocs(loaded);
    };

    return (
        <div
            role="region"
            aria-label="Bulk print"
            style={{
                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                padding: "10px 16px", background: "#eff6ff",
                borderBottom: `1px solid ${T.border}`,
            }}
        >
            <span style={{ fontSize: 13, fontWeight: 600, color: T.accent }}>
                {plural(selectedRows.length, "document")} selected
            </span>
            <button
                type="button"
                onClick={start}
                disabled={loading}
                style={{ ...btnStylePrimary, opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer" }}
            >
                <FiPrinter size={13} /> {loading ? "Preparing…" : "Print / Save PDF Selected"}
            </button>
            {canSend && (
                <button
                    type="button"
                    onClick={startSend}
                    disabled={loading || sendable.length === 0}
                    title={sendable.length === 0 ? "Only Approved documents can be sent" : `Email each document to its ${sendParty}`}
                    style={{
                        ...btnStyleOutline,
                        opacity: loading || sendable.length === 0 ? 0.5 : 1,
                        cursor: loading || sendable.length === 0 ? "not-allowed" : "pointer",
                    }}
                >
                    <FiSend size={13} /> Send Selected{sendable.length ? ` (${sendable.length})` : ""}
                </button>
            )}
            {children}
            <button
                type="button"
                onClick={onClear}
                disabled={loading}
                style={{
                    background: "none", border: "none", color: T.textMuted,
                    fontSize: 13, fontWeight: 500, cursor: loading ? "not-allowed" : "pointer",
                }}
            >
                Clear selection
            </button>
            {message && (
                <span role="alert" style={{ fontSize: 13, color: T.danger }}>{message}</span>
            )}
            {sendNotice && (
                <span
                    role={sendNotice.type === "danger" ? "alert" : "status"}
                    style={{ fontSize: 13, flexBasis: "100%", color: sendNotice.type === "danger" ? T.danger : T.success }}
                >
                    {sendNotice.msg}
                </span>
            )}

            {sendDocs && (
                <SendDocumentDialog
                    docs={sendDocs}
                    docType={docType}
                    party={sendParty}
                    Template={Template}
                    companyProfile={companyProfile}
                    forceNegativeTotals={forceNegativeTotals}
                    onClose={() => setSendDocs(null)}
                    onDone={setSendNotice}
                />
            )}

            {docs && (
                <div style={{ display: "none" }}>
                    <div ref={printRef}>
                        {docs.map((doc, index) => (
                            <div
                                key={doc.id ?? index}
                                data-testid="bulk-print-document"
                                style={index < docs.length - 1 ? { breakAfter: "page", pageBreakAfter: "always" } : undefined}
                            >
                                <Template
                                    data={doc}
                                    companyProfile={companyProfile}
                                    docType={docType}
                                    taxRate={0}
                                    forceNegativeTotals={forceNegativeTotals}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
